import React, { useEffect, useState } from "react";
import { apiUtils } from "../../../utils/apiUtils";
import { PATHS } from "../../../constants/Paths";

/**
 * Plan & usage - READ-ONLY for customers during the pilot.
 *
 * The previous version let the customer pick their own plan AND their own
 * subscription status - including setting themselves to "Active" - from
 * dropdowns and save it, with no payment step. That is internal tooling
 * accidentally shipped to customers ("Billing and Plan Enforcement", literally
 * its old title). Plan changes now go through us; the panel shows what the
 * workspace has and what it has used.
 */
export default function RepCheckBillingAdmin() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError("");
        const response = await apiUtils.get("/api/users/billing");
        setBilling(response.data?.data || null);
      } catch (loadError) {
        setError(loadError.message || "Failed to load plan details.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const statusTone = {
    active: "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300",
    trialing: "bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300",
    past_due: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
    canceled: "bg-muted text-muted-foreground",
  };
  const status = billing?.subscription?.status || "trialing";

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Plan &amp; usage</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Your current plan and this period's usage.
          </p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${statusTone[status] || statusTone.trialing}`}>
          {status.replace("_", " ")}
        </span>
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : (
        <>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-muted p-4">
              <div className="text-sm text-muted-foreground">Plan</div>
              <div className="mt-2 text-lg font-semibold text-foreground truncate" title={billing?.plan?.name || "Pilot"}>
                {billing?.plan?.name || "Pilot"}
              </div>
            </div>
            <div className="rounded-xl border border-border bg-muted p-4">
              <div className="text-sm text-muted-foreground">Accounts used</div>
              <div className="mt-2 text-2xl font-semibold text-foreground">
                {billing?.usage?.accounts ?? 0}
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  / {billing?.plan?.limits?.accounts ?? "Unlimited"}
                </span>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-muted p-4">
              <div className="text-sm text-muted-foreground">Scans this period</div>
              <div className="mt-2 text-2xl font-semibold text-foreground">
                {billing?.usage?.scansThisPeriod ?? 0}
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  / {billing?.plan?.limits?.scansPerMonth ?? "Unlimited"}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-border p-4">
            <div className="text-sm font-semibold text-foreground">Included in your plan</div>
            <ul className="mt-3 space-y-2 text-sm text-foreground">
              <li>RepGuard draft review: {billing?.plan?.features?.repguard ? "Included" : "Not included"}</li>
              <li>Scheduled monitoring: {billing?.plan?.features?.scheduledMonitoring ? "Included" : "Not included"}</li>
              <li>Advanced reports: {billing?.plan?.features?.advancedReports ? "Included" : "Not included"}</li>
            </ul>
          </div>

          <p className="mt-6 text-sm text-muted-foreground">
            Need a different plan or higher limits?{" "}
            <a href={PATHS.SUPPORT} className="font-medium text-brand-600 hover:text-brand-700">
              Contact us
            </a>{" "}
            and we'll adjust it for you.
          </p>
        </>
      )}
    </div>
  );
}
